/**
 * Centralized validation, success, and confirmation messages per spec (Validations 3.txt).
 * Use these constants for consistent wording across Site, Onboarding, Visit, Report, and CRF modules.
 */

// Common (e.g. post–e-sign success message for all authenticate transactions)
export const common = {
  authSuccess: 'Saved Successfully.',
};

// Site Proposed & Selection
export const sites = {
  investigatorRequired: 'Please select Investigator.',
  siteNameRequired: 'Please enter Site Name.',  
  siteNameMinLength: 'Site Name must be at least 3 characters.',
  siteCodeRequired: 'Please enter Site Code.',
  countryRequired: 'Please select Country.',
  recruitmentRequired: 'Please enter No. Of Recruitment Projections.',
  recruitmentInvalidNumber: 'Please enter valid Number.',
  recruitmentNegative: 'Value cannot be negative.',
  savedSuccess: 'Saved Successfully.',
  /** Site Feasibility: inactivate site blocked while scheduled visits are still active (API returns same text; toast only in UI). */
  cannotInactiveWithActiveVisits:
    'This site cannot be set inactive while it has active scheduled visits.',
  assignCRA: 'Please assign CRA.',
  emailInvalid: 'Please enter valid Email Address.',
  /** Add New Site: must change something from the initial / pre-filled state before save (e-sign). */
  atLeastOneChangeRequired: 'Please update at least one value.',
  // AI Suggested Sites
  aiSelectOneSite: 'Please select at least one Site from the list.',
  aiPiNotInSystem: 'The following PI name(s) are not registered in the Gateway.',
  aiRequiredFieldsBlank: 'Some selected sites have missing required data (PI Name, Site Name, or Country). Opening Add New Site with available data for you to complete.',
  aiSitesFilteredByPiName:
    'All returned sites were hidden because the PI field did not look like a person\'s name (roles, titles, or blank PI are excluded). Try Get AI Suggestions again or choose another country.',
  aiNoSitesForSelectedCountries:
    'No data found for the selected countries.',
  /** Shown when the external AI / integrated provider is unavailable or returns an error (hide technical API text). */
  aiIntegratedSourceUnavailable:
    'Unable to retrieve data from the integrated source system.',
  /** AI feature disabled or misconfigured in appsettings (replaces raw server text). */
  aiSuggestionNotEnabled:
    'AI site suggestions are not enabled. Please contact your administrator.',
  /** Model returned no usable content (replaces raw server text). */
  aiSuggestionEmptyResponse:
    'Service is currently unavailable. Please try again later.',
  /**
   * Fallback when the integrated source fails in a way that has no specific validation message
   * (replaces raw JSON, stack traces, or provider error bodies on the UI).
   */
  aiSuggestionUnableToComplete:
    'Unable to retrieve AI site suggestions. Please try again later.',
};

// Site Onboarding
export const onboarding = {
  selectOneSite: 'Please select at least one Site to Onboard.',
  siteCodeRequired: 'Please select Site',
  siteCodeDuplicate: 'Site already exists for this project.',
  frequencyRequired: 'Please select visit Frequency.',
  siteVisitsRequired: 'Please enter number of Site Visits.',
  siteVisitsMin: 'Site visits must be at least 1.',
  siteVisitsPositiveInteger: 'Please enter valid Number of Site Visits.',
  /** Message when visits exceed max for frequency. Use with .replace('{max}', n).replace('{frequency}', label) */
  siteVisitsMaxForFrequency: 'Site visits cannot exceed {max} for {frequency}.',
  /** API messageKey ssvIncomplete — full copy only here. */
  ssvIncomplete: 'All Site Selection Visits (SSV) must be completed before onboarding.',
  sivScheduledLock: 'Site Onboarding is locked because Site Initiation Visit is already scheduled for this site.',
};

// Monitoring Visit & Report Management
export const visits = {
  siteRequired: 'Please select Site.',
  visitTypeRequired: 'Please select Visit Type.',
  visitTypeRequiredAlt: 'Please select Visit Type.',
  craRequired: 'Please assign CRA.',
  startDateRequired: 'Please select Scheduled Start Date.',
  startDateRequiredAlt: 'Please select start date.',
  endDateRequired: 'Please select Scheduled End Date.',
  endDateRequiredAlt: 'Please select End Date.',
  startAfterEnd: 'Start Date cannot be after End Date.',
  endBeforeStart: 'End Date cannot be before Start Date.',
  visitDatePresentOrFuture: 'Visit date must be present date or future date.',
  projectStudyRequired: 'Please select a valid project. A matching study is required.',
  ssvAlreadyOnboarded: 'Site Selection Visit has already been onboarded for this site.',
  completeSsvOnboarding: 'Please complete SSV on-boarding',
  savedSuccess: 'Saved Successfully.',
  inactiveConfirm: 'Are you sure you want to mark this Visit as Inactive?',
  letterBodyEmpty: 'Visit Letter Content cannot be empty.',
  letterBodyShort: 'Please provide complete Visit details.',
  letterPreviewEmpty: 'Letter Content is empty.',
  noDataToSave: 'Visit Letter Content cannot be empty.',
  letterSubmitEmpty: 'Please enter Content in the Letter Body before saving.',
  confirmationModeRequired: 'Please select Confirmation Mode.',
  confirmedOnRequired: 'Please select Confirmation Date.',
  confirmedOnNotPast: 'Confirmation Date must be today or a future date.',
  confirmedOnBeforeScheduledVisit: 'Confirmation Date cannot be before the Scheduled Visit Date.',
  confirmedOnBeforeVisitLetter: 'Confirmation Date cannot be before the Generate Visit Letter date.',
  confirmationRecipientRequired: 'Please select Confirmation Sent To.',
  actualStartRequired: 'Please enter Actual Start Date.',
  actualStartFuture: 'Actual Start Date cannot be in the Future.',
  actualStartBeforeScheduled: 'Actual Start Date cannot be before Scheduled Start Date.',
  actualEndRequired: 'Please enter Actual End Date.',
  actualEndBeforeStart: 'Actual End Date cannot be before Actual Start Date.',
  actualEndFuture: 'Actual End Date cannot be in the Future.',
  actualEndBeforeScheduled: 'Actual End Date cannot be before Scheduled Start Date.',
  outsideRangeConfirm: 'Actual Visit Dates are outside Scheduled Range. Do you want to Continue?',
};

// In Progress – Start Report (CRFForm)
export const report = {
  draftOneValue: 'Please enter at least one value before saving the draft.',
  noDataToSave: 'No Data to Save',
  printAfterDraftOrSubmit: 'Printing is allowed only after the Record is saved as Draft or Submitted.',
  allFieldsRequired: 'All fields are required',
  updateDcfGeneratedField: 'Please update DCF generated field.',
  noTemplateAssigned: 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).',
  templateMappingInactive: 'Template mapping is inactive. Please ensure an active template is mapped in Report Management (Map Template to Visit).',
  pdfGenerationFailed:
    'Cant Generate the PDF Size is too long.',
};

// First/Second Review & DCF
export const review = {
  passwordRequired: 'Please enter Password',
  remarksRequired: 'Please enter Remarks',
};

export const dcf = {
  remarksRequired: 'Please enter Remarks.',
  alreadyRaised: 'A DCF is already raised on this Field. It must be resolved by CRA and then closed before you can raise another.',
  remarksOrUpdateRequired: 'Please Enter DCF Remarks.',
};

// Attribute & Template Management (CRF Management)
export const crf = {
  attributeGroupName: 'Please enter Report Group',
  attributeGroupDuplicate: 'Report Group with this name already exists.',
  remarksRequired: 'Please enter Remarks',
  attributeSubGroupName: 'Please enter Report Sub Group',
  attributeSubGroupDuplicate: 'Report Sub Group with this name already exists.',
  attributeDescription: 'Please enter Field Description',
  attributeDescriptionDuplicate: 'Field Description with this name already exists.',
  attributeGroupSelect: 'Please select Report Group',
  attributeSubGroupSelect: 'Please select Report Sub Group',
  attributeTypeSelect: 'Please select Report Type',
  attributeLabelValueRequired: 'Please enter Field Value.',
  attributeValuesInvalid: 'Please enter valid comma separated values.',
  activeRequired: 'Please select active status.',
  notNullRequired: 'Please select Not Null option.',
  templateNameRequired: 'Please enter Template Name',
  selectOneAttribute: 'Please select at least one attribute.',
  onlyDraftEditable: 'Only Draft Templates can be edited.',
  templateInUse: 'Template cannot be edited as it is already in Use.',
  templateAlreadyMapped: 'Template is already mapped to selected Visit.',
  inactiveTemplateCannotMap: 'Inactive CRF templates cannot be assigned to a visit. Please select an Active template.',
  deleteConfirm: 'Are you sure you want to delete this Template?',
  deleteInUse: 'Template cannot be deleted as it is already used in a transaction.',
  templateDeleted: 'Saved Successfully.',
  savedSuccess: 'Saved Successfully.',
  savedSuccessLower: 'Saved Successfully.',
  cannotRemoveMapping:
    'Mapped Template cannot be removed as it is already in use.',
};

// Create Action Item (Monitoring Visit & Report Management)
export const actionItems = {
  visitTypeRequired: 'Please select Visit Type',
  visitTypeRequiresSite: 'Please select Site before selecting Visit Type.',
  siteRequired: 'Please select Site',
  assignActionItemToRequired: 'Please select Assign Issue',
  documentSectionsRequired: 'Please select Document Sections',
  sectionAttributesRequired: 'Please select Section Attributes',
  actionItemDescriptionRequired: 'Please enter Issue Description',
  assignedToRequired: 'Please select Assignee',
  dueDateRequired: 'Please select date',
  dueDateNoPast: 'Due date cannot be in the past.',
  dateOfAwarenessRequired: 'Please select Date of Awareness',
  dateOfAwarenessNoFuture: 'Date of Awareness cannot be in the future.',
  dateOfIssueRequired: 'Please select Date of Issue',
  dateOfIssueMustBeToday: 'Date of Issue must be today.',
  issueCategoryRequired: 'Please select Issue Category',
  priorityRequired: 'Please select Priority',
  visitRequired: 'Please select Visit.',
  savedSuccess: 'Saved Successfully.',
  savesSuccess: 'Saved Successfully.', // alias for create action item success
  actionItemAlreadyExistsForField: 'An open issue already exists for this field. Close it before creating another.',
  crfHeaderDetailRequired: 'CRF header and detail are required to save this issue. Please open the issue from the Visit report row or ensure a valid question is selected.',
  pleaseDraftSaveFirst: 'Please do the Draft Save First.',
  replyRemarkRequired: 'Action Item Description is required.',
  remarkSavedSuccess: 'Saved Successfully.',
  issueSubmittedToCreatorSuccess: 'Saved Successfully.',
};

export default {
  sites,
  onboarding,
  visits,
  report,
  review,
  dcf,
  crf,
  actionItems,
};
